import { cn } from "@/lib/utils";
import type { DetailOverview } from "@/services/navigator/agent-detail-service";

import { NvBuildingIcon, NvListIcon, NvUserIcon } from "./detail-icons";
import { ChipGroup, ClampText, DetailLabel, DetailSection, DetailSubTitle, Paragraphs } from "./detail-parts";

// 개요 (설계서 slide 41 · Figma 03-01/04/05): 해결 과제 큰 문장(2차까지) 또는 첫 문장 리드(1차+WF · 1차만, 90자 이하) → 본문 문단 →
// 강점 영역 → 도입 대상 3카드(2차까지) → 차별화 포인트 카드(2차까지 · 1차+WF). 우측 팩트(지원 데이터 · 제공 인터페이스)가 모두 비면 본문을 전체 폭으로.

// 한 줄 최대 4개: 4개(또는 4의 배수)면 4열, 그 밖에는 3열 (Figma 03-04는 5개를 3 + 2로)
const differentiatorColumns = (n: number) => (n === 4 || (n > 4 && n % 4 === 0) ? "xl:grid-cols-4" : "xl:grid-cols-3");

// 도입 대상 카드의 아이콘 타일 (Figma ver.7: 36 · 반경 10, 타일 색 + 같은 계열 아이콘 색)
const AUDIENCE_ICONS = {
  "사용 부서": { Icon: NvUserIcon, tile: "bg-[#FFF0E8] text-[#FD5108]" },
  "도입 가능 기업": { Icon: NvBuildingIcon, tile: "bg-[#FFF4E0] text-[#EB8C00]" },
  준비사항: { Icon: NvListIcon, tile: "bg-[#FCEBEF] text-[#D93954]" },
} as const;

export function OverviewSection({ data, first }: { data: DetailOverview; first?: boolean }) {
  const hasFacts = data.data_modalities.length > 0 || data.interfaces.length > 0;
  const hasText = !!(data.headline || data.lead || data.paragraphs.length);
  return (
    <DetailSection id="overview" title="개요" first={first}>
      <div className="flex flex-col">
        {(hasText || hasFacts) && (
          <div className={cn("grid gap-6", hasFacts && hasText ? "lg:grid-cols-[minmax(0,1fr)_363px]" : "grid-cols-1")}>
            {hasText && (
              <div className="flex min-w-0 flex-col gap-[18px]">
                {data.headline && (
                  <div className="flex flex-col gap-[6px]">
                    <DetailLabel>해결 과제</DetailLabel>
                    <p className="text-[24px] font-normal leading-[34px] text-nv-text-primary xl:text-[30px] xl:leading-[42px]">{data.headline}</p>
                  </div>
                )}
                {/* 1차+WF · 1차만: 개요 첫 문장(90자 이하)은 큰 글씨 리드 30px (Figma ver.7 03-05, 정의서 01 개요 리드) */}
                {data.lead && <p className="text-[24px] font-normal leading-[34px] text-nv-text-primary xl:text-[30px] xl:leading-[42px]">{data.lead}</p>}
                <Paragraphs items={data.paragraphs} />
              </div>
            )}
            {hasFacts && (
              <div className="flex min-w-0 flex-col gap-[22px] self-start">
                <ChipGroup label="지원 데이터" values={data.data_modalities} />
                <ChipGroup label="제공 인터페이스" values={data.interfaces} />
              </div>
            )}
          </div>
        )}

        {data.strength && (
          <div className="mt-7 flex max-w-[749px] flex-col gap-0 border-l-[3px] border-nv-brand pb-2 pl-[17px] pt-[6px]">
            <span className="text-[16px] leading-[27px] text-nv-text-tertiary">강점 영역</span>
            <ClampText className="text-[16px] leading-[27px] text-nv-text-primary">{data.strength}</ClampText>
          </div>
        )}

        {data.audience.length > 0 && (
          <div className="mt-14 flex flex-col gap-[18px]">
            <DetailSubTitle>도입 대상</DetailSubTitle>
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(0,1fr))]">
              {data.audience.map((a) => {
                const { Icon, tile } = AUDIENCE_ICONS[a.label as keyof typeof AUDIENCE_ICONS] ?? AUDIENCE_ICONS["사용 부서"];
                return (
                  <div key={a.label} className="flex min-h-[160px] flex-col gap-[6px] rounded-nv-card border border-nv-border bg-nv-surface p-6 pb-[23px]">
                    <span aria-hidden className={cn("-mb-px -ml-[7px] -mt-2 flex h-9 w-9 items-center justify-center rounded-[10px]", tile)}>
                      <Icon size={22} />
                    </span>
                    <span className="text-[15px] leading-6 text-nv-text-primary">{a.label}</span>
                    <p className="text-[15px] leading-6 text-nv-text-secondary">{a.value}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data.differentiators.length > 0 && (
          <div className="mt-14 flex flex-col gap-[18px]">
            <DetailSubTitle>차별화 포인트</DetailSubTitle>
            {/* 한 줄 최대 4개, 카드 높이는 전체 중 최대로 통일 (grid 행 높이 = 가장 큰 카드) */}
            <div className={cn("grid auto-rows-fr gap-6 md:grid-cols-2", differentiatorColumns(data.differentiators.length))}>
              {data.differentiators.map((d, i) => (
                <div key={i} className="flex flex-col gap-[10px] rounded-nv-card border border-nv-border bg-nv-surface px-6 pb-[23px] pt-[26px]">
                  <span className="text-[13px] leading-[21px] text-nv-text-tertiary">{String(i + 1).padStart(2, "0")}</span>
                  {d.name && <span className="text-[16px] leading-[23px] text-nv-text-primary">{d.name}</span>}
                  {d.description && <p className="text-[15px] leading-6 text-nv-text-secondary">{d.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DetailSection>
  );
}
